import { useQuery, useReactiveVar } from '@apollo/client';
import { IconButton, Stack, Text } from '@fluentui/react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { setUserDefaults } from 'utility/cache/ui';
import { ColumnsHeader } from './ColumnHeader';
import FormField from './FormField';
import { getColumns } from './columns.data';
import { useStyles } from './index.styles';
import { getRefCodes } from './utils';
import { DistributionRowTotal } from './DistributionRowTotal';
import { InvoiceDetails_invoice } from '../graphql/__generated__/InvoiceDetails';
import { TransactionSigningValues } from 'common/components/Modules/TransactionEdit/types';
import { AvailablePurchaseOrders } from '../graphql/__generated__/AvailablePurchaseOrders';
import { InvoiceTransactionTypes } from '../graphql/__generated__/InvoiceTransactionTypes';
import { getDefaultDistribution } from '../utils';
import { ReferenceCodeList } from 'common/graphql/__generated__/ReferenceCodesList';

import REFERENCE_CODE from '../../../../graphql/ReferenceCodesList.graphql';
interface AccountingProps {
  invoiceData?: Partial<InvoiceDetails_invoice>;
  invoiceDistributionStatusTypes: InvoiceTransactionTypes | undefined;
  availablePurchaseOrders: AvailablePurchaseOrders | undefined;
  isSupplierViewModal?: boolean;
}

export const Accounting: React.FC<AccountingProps> = ({
  invoiceData,
  invoiceDistributionStatusTypes,
  availablePurchaseOrders,
  isSupplierViewModal = false,
}) => {
  const userDefaults = useReactiveVar(setUserDefaults);
  const { columnArray } = getColumns(userDefaults);
  const watch = useWatch<TransactionSigningValues>();
  const { control, getValues } = useFormContext<TransactionSigningValues>();
  const { fields, remove, append } = useFieldArray({
    name: 'invoiceDistributions',
    control,
  });
  const styles = useStyles();
  const renderRef = useRef<boolean>(true);

  const { data: referenceCodes } = useQuery<ReferenceCodeList>(REFERENCE_CODE, {
    fetchPolicy: 'cache-first',
  });

  const refCodesOptions = useMemo(
    () => getRefCodes(referenceCodes),
    [referenceCodes]
  );
  const invoiceRows = getValues('invoiceDistributions');
  const isDataInSync = invoiceRows?.length === fields.length;
  const lastItem = invoiceRows?.[invoiceRows.length - 1];

  const isValueExist = isDataInSync && lastItem
      ? Object.entries(lastItem).reduce((prev, [key, value]) => {
        if (
        (key === 'referenceCode6' &&
          value === userDefaults?.referenceCode6) ||
        (key === 'referenceCode7' &&
          value === userDefaults?.referenceCode7) ||
        (key === 'projectReference' &&
          value === userDefaults?.projectReference) ||
          (key === 'setReference' && value === userDefaults?.setReference)
        )
          return prev;
        const isValue = value !== null && value !== undefined;
        return isValue || prev;
      }, false)
      : false;

  const isEdit = !!invoiceData?.id;
  const { _isUpdatable } = { ...invoiceData };
  const isUpdatable = isEdit ? !!_isUpdatable : true;

  useEffect(() => {
    if (isValueExist && !renderRef.current && isUpdatable) {
      const values = getDefaultDistribution(userDefaults);
      append(values, { shouldFocus: false });
    }
    renderRef.current = false;
  }, [append, isValueExist, userDefaults, isUpdatable]);
  const seletedTransactionType =
    invoiceDistributionStatusTypes?.invoiceTransactionTypes?.nodes.find(
      (item) => item.id === watch.transactionTypeId
    );

  const isAccountRowHidden =
    seletedTransactionType?.layoutType === 'SIGNING_DOCUMENT' ||
    seletedTransactionType?.layoutType === 'SIGNING_AMOUNT_DOCUMENT';
  if (isAccountRowHidden) return null;
  return (
    <Stack tokens={{ padding: '0px 0px 30px 0px' }}>
      <Stack tokens={{ padding: 20 }}>
        <Text variant="xLarge">Accounting</Text>
      </Stack>
      <Stack className={styles.scrollContainer}>
        <Stack tokens={{ padding: '20px 0px 40px' }}>
          <Stack horizontal style={{ overflowX: 'clip' }}>
            {fields.length > 1 && (
              <Stack tokens={{ padding: '52px 0px', childrenGap: 3 }}>
                {fields.map((_, index) => {
                  const isNotLastItem = fields.length - 1 !== index;
                  return (
                    <Stack key={index} style={{ height: 33, width: 40 }}>
                      {!isSupplierViewModal && isNotLastItem && (
                        <IconButton
                          iconProps={{ iconName: 'Blocked2Solid' }}
                          ariaLabel="delete"
                          onClick={() => remove(index)}
                          style={{ color: 'red' }}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            )}
            <Stack
              style={{
                overflowX: 'scroll',
                padding: '0px 0px 40px 0px',
              }}
            >
              <ColumnsHeader columnArray={columnArray} />
              {fields.map((field, index) => {
                const baseField = `invoiceDistributions.${index}`;
                return (
                  <Stack key={index} horizontal tokens={{ childrenGap: 10 }}>
                    {columnArray.map((value, key) => {
                      return (
                        <FormField
                          index={index}
                          removeCurrentIndex={() => remove(index)}
                          baseField={baseField}
                          columnData={value}
                          key={field.id + key}
                          refCodesOptions={refCodesOptions}
                          invoiceData={invoiceData}
                          invoiceDistributionStatusTypes={
                            invoiceDistributionStatusTypes
                          }
                          availablePurchaseOrders={availablePurchaseOrders}
                          isDisabled={isSupplierViewModal}
                        />
                      );
                    })}
                  </Stack>
                );
              })}
              <DistributionRowTotal columnsArray={columnArray} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
